import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@koridor/api-client';
import { DEFAULT_SETTINGS, type User } from '@koridor/shared';

const CACHED_USER_KEY = 'koridor_cached_user';
const GUEST_FLAG_KEY = 'koridor_guest_session';
export const GUEST_USER_ID = 'guest';

export function createGuestUser(): User {
  return {
    id: GUEST_USER_ID,
    publicId: 'GUEST',
    nickname: 'Гость',
    email: null,
    rating: 1000,
    peakRating: 1000,
    rank: 'Любитель',
    avatarUrl: null,
    online: false,
    aiRating: 1200,
    aiWinStreak: 0,
    aiLossStreak: 0,
    settings: { ...DEFAULT_SETTINGS },
  };
}

export function isGuestUser(user: User | null | undefined): boolean {
  return !!user && user.id === GUEST_USER_ID;
}

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as User;
    if (isGuestUser(user)) return null;
    return user;
  } catch {
    return null;
  }
}

function writeCachedUser(user: User | null) {
  try {
    if (!user || isGuestUser(user)) localStorage.removeItem(CACHED_USER_KEY);
    else localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
  } catch {
    /* ignore quota / private mode */
  }
}

function readGuestFlag(): boolean {
  try {
    return localStorage.getItem(GUEST_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function writeGuestFlag(on: boolean) {
  try {
    if (on) localStorage.setItem(GUEST_FLAG_KEY, '1');
    else localStorage.removeItem(GUEST_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isGuest: boolean;
  /** True when UI has a user but API has no access token (guest or cached offline). */
  isOfflineSession: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (nickname: string, email: string, password: string) => Promise<void>;
  appleLogin: (identityToken: string, nickname?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  enterGuest: () => void;
  /** Re-attempt refresh for a previously authenticated (cached) user when network returns. */
  tryRestoreAuthenticated: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const adoptUser = useCallback((u: User | null, opts?: { guest?: boolean }) => {
    const guest = opts?.guest ?? isGuestUser(u);
    setUser(u);
    writeGuestFlag(guest);
    if (guest) {
      /* guest is not a durable account cache */
      return;
    }
    writeCachedUser(u);
  }, []);

  const enterGuest = useCallback(() => {
    api.clearTokens();
    adoptUser(createGuestUser(), { guest: true });
  }, [adoptUser]);

  const tryRestoreAuthenticated = useCallback(async (): Promise<boolean> => {
    if (isGuestUser(user) || readGuestFlag()) return false;
    const result = await api.restoreSessionDetailed();
    if (result !== 'authenticated') return false;
    try {
      const me = await api.getMe();
      adoptUser(me, { guest: false });
      api.connectWs();
      return true;
    } catch {
      return false;
    }
  }, [adoptUser, user]);

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.restoreSessionDetailed();
        if (result === 'authenticated') {
          const me = await api.getMe();
          adoptUser(me, { guest: false });
          return;
        }

        if (result === 'rejected') {
          // Explicit auth failure — drop durable profile cache.
          writeCachedUser(null);
          writeGuestFlag(false);
          return;
        }

        // network | none — prefer cached real user; do not wipe cache on network blips.
        const cached = readCachedUser();
        if (cached) {
          adoptUser(cached, { guest: false });
          return;
        }

        // Returning guest session (previous "play offline") only when flag set.
        if (readGuestFlag()) {
          adoptUser(createGuestUser(), { guest: true });
        }
      } catch {
        const cached = readCachedUser();
        if (cached) {
          adoptUser(cached, { guest: false });
          return;
        }
        if (readGuestFlag()) {
          adoptUser(createGuestUser(), { guest: true });
          return;
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [adoptUser]);

  // When network returns, re-auth cached real users (not guests).
  useEffect(() => {
    if (loading) return;
    const onOnline = () => {
      if (isGuestUser(user) || api.isAuthenticated) return;
      void tryRestoreAuthenticated();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [loading, user, tryRestoreAuthenticated]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: u } = await api.login(email, password);
    adoptUser(u, { guest: false });
    api.connectWs();
  }, [adoptUser]);

  const register = useCallback(async (nickname: string, email: string, password: string) => {
    const { user: u } = await api.register(nickname, email, password);
    adoptUser(u, { guest: false });
    api.connectWs();
  }, [adoptUser]);

  const appleLogin = useCallback(async (identityToken: string, nickname?: string) => {
    const { user: u } = await api.appleSignIn(identityToken, nickname);
    adoptUser(u, { guest: false });
    api.connectWs();
  }, [adoptUser]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      api.clearTokens();
    }
    writeGuestFlag(false);
    writeCachedUser(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!api.isAuthenticated) return;
    const u = await api.getMe();
    adoptUser(u, { guest: false });
  }, [adoptUser]);

  const isGuest = isGuestUser(user);
  const isOfflineSession = !!user && !api.isAuthenticated;

  const value = useMemo(() => ({
    user,
    loading,
    isGuest,
    isOfflineSession,
    login,
    register,
    appleLogin,
    logout,
    refreshUser,
    enterGuest,
    tryRestoreAuthenticated,
  }), [
    user, loading, isGuest, isOfflineSession,
    login, register, appleLogin, logout, refreshUser, enterGuest, tryRestoreAuthenticated,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside provider');
  return ctx;
}
